<template>
  <el-dialog
    title="📝 我的答题记录"
    :visible.sync="visible"
    width="75%"
    top="5vh"
    @close="handleClose"
  >
    <!-- 统计卡片 -->
    <el-row :gutter="16" style="margin-bottom: 20px;" v-loading="statsLoading">
      <el-col :span="6">
        <el-card shadow="hover" class="stats-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
            <i class="el-icon-edit-outline"></i>
          </div>
          <div class="stat-info">
            <div class="stat-label">答题次数</div>
            <div class="stat-value">{{ statistics.totalAttempts || 0 }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stats-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);">
            <i class="el-icon-question"></i>
          </div>
          <div class="stat-info">
            <div class="stat-label">做过的题</div>
            <div class="stat-value">{{ statistics.uniqueQuestions || 0 }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stats-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);">
            <i class="el-icon-success"></i>
          </div>
          <div class="stat-info">
            <div class="stat-label">答对次数</div>
            <div class="stat-value">{{ statistics.correctCount || 0 }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stats-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);">
            <i class="el-icon-data-analysis"></i>
          </div>
          <div class="stat-info">
            <div class="stat-label">正确率</div>
            <div class="stat-value">{{ statistics.correctRate || '0%' }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 题目详情 -->
    <div v-if="questionDetails && questionDetails.length > 0" style="margin-top: 20px;">
      <h3 style="margin-bottom: 15px;">📊 题目详情</h3>
      <el-collapse accordion>
        <el-collapse-item
          v-for="(item, index) in questionDetails"
          :key="item.questionId"
          :name="item.questionId"
        >
          <template slot="title">
            <div style="display: flex; align-items: center; width: 100%; padding-right: 20px;">
              <el-tag
                :type="item.everCorrect ? 'success' : 'danger'"
                size="small"
                style="margin-right: 10px;"
              >
                {{ item.everCorrect ? '✅ 已答对' : '❌ 未答对' }}
              </el-tag>
              <span style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                题目{{ index + 1 }}: {{ item.questionContent || '无题目内容' }}
              </span>
              <el-tag type="info" size="mini" style="margin-left: 10px;">
                答了 {{ item.attemptCount }} 次
              </el-tag>
            </div>
          </template>

          <div style="padding: 15px; background: #f5f7fa; border-radius: 4px;">
            <p style="margin: 8px 0;">
              <b>题目ID：</b>{{ item.questionId }}
            </p>
            <p style="margin: 8px 0;">
              <b>题目内容：</b>{{ item.questionContent || '无' }}
            </p>
            <p style="margin: 8px 0;">
              <b>正确答案：</b>
              <el-tag type="success" size="small">{{ item.correctAnswer || '无' }}</el-tag>
            </p>
            <p style="margin: 8px 0;">
              <b>尝试次数：</b>{{ item.attemptCount }}
            </p>
            <p style="margin: 8px 0;">
              <b>最后结果：</b>
              <el-tag :type="item.lastCorrect ? 'success' : 'danger'" size="small">
                {{ item.lastCorrect ? '✅ 正确' : '❌ 错误' }}
              </el-tag>
            </p>
            <p style="margin: 8px 0;">
              <b>最后答题时间：</b>{{ formatTime(item.lastAnswerTime) }}
            </p>

            <!-- 查看该题详细记录 -->
            <el-button
              type="primary"
              size="mini"
              style="margin-top: 10px;"
              @click="viewQuestionRecords(item.questionId)"
            >
              查看详细记录
            </el-button>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>

    <div v-else style="text-align: center; padding: 40px; color: #909399;">
      <i class="el-icon-document" style="font-size: 48px; margin-bottom: 10px;"></i>
      <p>暂无答题记录</p>
    </div>

    <!-- 题目详细记录对话框 -->
    <el-dialog
      title="📋 题目答题记录"
      :visible.sync="detailRecordsVisible"
      width="70%"
      append-to-body
    >
      <el-table
        :data="detailRecords"
        v-loading="detailLoading"
        border
        stripe
        style="width: 100%"
      >
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="attemptCount" label="第几次" width="80" align="center">
          <template slot-scope="{row}">
            <el-tag type="info" size="small">第{{ row.attemptCount }}次</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="userAnswer" label="我的答案" width="100" align="center">
          <template slot-scope="{row}">
            <el-tag size="medium">{{ row.userAnswer }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="结果" width="100" align="center">
          <template slot-scope="{row}">
            <el-tag :type="row.isCorrect ? 'success' : 'danger'" effect="dark">
              {{ row.isCorrect ? '✅ 正确' : '❌ 错误' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="answerTimeCost" label="用时(秒)" width="90" align="center" />
        <el-table-column label="答题时间" width="160" align="center">
          <template slot-scope="{row}">
            {{ formatTime(row.answerTimestamp) }}
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { getMyVideoAnswers, getMyVideoStatistics } from '@/api/amcVideoEnhancement'

export default {
  name: 'StudentVideoAnswerRecords',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    courseId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      statistics: {},
      questionDetails: [],
      statsLoading: false,
      detailRecordsVisible: false,
      detailRecords: [],
      detailLoading: false,
      currentQuestionId: null
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadStatistics()
      }
    }
  },
  methods: {
    async loadStatistics() {
      try {
        this.statsLoading = true
        const res = await getMyVideoStatistics(this.courseId)
        if (res && res.data) {
          this.statistics = res.data
          this.questionDetails = res.data.questionDetails || []
        }
      } catch (e) {
        console.error('加载统计数据失败', e)
        this.$message.error('加载统计数据失败')
      } finally {
        this.statsLoading = false
      }
    },
    async viewQuestionRecords(questionId) {
      try {
        this.detailLoading = true
        this.currentQuestionId = questionId
        this.detailRecordsVisible = true

        const res = await getMyVideoAnswers(this.courseId, null)
        if (res && res.data) {
          // 过滤出该题目的记录
          this.detailRecords = res.data.filter(r => r.questionId === questionId)
        }
      } catch (e) {
        console.error('加载详细记录失败', e)
        this.$message.error('加载详细记录失败')
      } finally {
        this.detailLoading = false
      }
    },
    formatTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    handleClose() {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.stats-card {
  display: flex;
  align-items: center;
  padding: 16px;
  transition: all 0.3s;
}

.stats-card:hover {
  transform: translateY(-4px);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
  margin-right: 16px;
}

.stat-info {
  flex: 1;
}

.stat-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
}

::v-deep .el-collapse-item__header {
  font-size: 14px;
  padding: 12px 0;
}
</style>
